import { useMemo } from 'react';
import useSWR from 'swr';

import { post } from 'common/apiClient';


import { createValidation } from './common';
import { emailRule } from './user';

import axios from 'axios';
import type { Rule } from 'antd/es/form';

// Safe getter function using Vite's import.meta.env
const getEnvVar = (key: string, defaultValue: string): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key] !== undefined) {
    return String(import.meta.env[key]).replace(/^["']|["']$/g, '');
  }
  return defaultValue;
};

const ENV_CONFIG = {
  minLength: Number(getEnvVar('VITE_PASSWORD_MIN_LENGTH', '12')) || 12,
  requireUppercase: getEnvVar('VITE_PASSWORD_REQUIRE_UPPERCASE', 'true') !== 'false',
  requireLowercase: getEnvVar('VITE_PASSWORD_REQUIRE_LOWERCASE', 'true') !== 'false',
  requireNumbers: getEnvVar('VITE_PASSWORD_REQUIRE_NUMBERS', 'true') !== 'false',
  requireSpecialChar: getEnvVar('VITE_PASSWORD_REQUIRE_SPECIAL_CHAR', 'true') !== 'false',
  messages: {
    minLength: getEnvVar('VITE_PASSWORD_MSG_MIN_LENGTH', 'Minimum 12 characters required.'),
    uppercase: getEnvVar('VITE_PASSWORD_MSG_UPPERCASE', 'At least 1 uppercase letter (A-Z) is required.'),
    lowercase: getEnvVar('VITE_PASSWORD_MSG_LOWERCASE', 'At least 1 lowercase letter (a-z) is required.'),
    numbers: getEnvVar('VITE_PASSWORD_MSG_NUMBERS', 'At least 1 number (0-9) is required.'),
    specialChar: getEnvVar('VITE_PASSWORD_MSG_SPECIAL', 'At least 1 special character (!@#$, etc.) is required.'),
  },
};

export const passwordPolicyRules: Rule[] = [
  { required: true, message: 'Please enter your password!' },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();

      const errors: string[] = [];

      const isMinLength = value.length >= ENV_CONFIG.minLength;
      const hasUppercase = /[A-Z]/.test(value);
      const hasLowercase = /[a-z]/.test(value);
      const hasNumbers = /[0-9]/.test(value);
      const hasSpecialChar = /[^a-zA-Z0-9]/.test(value);

      if (!isMinLength) {
        errors.push(`${ENV_CONFIG.messages.minLength} (Current: ${value.length}).`);
      }
      if (ENV_CONFIG.requireUppercase && !hasUppercase) {
        errors.push(ENV_CONFIG.messages.uppercase);
      }
      if (ENV_CONFIG.requireLowercase && !hasLowercase) {
        errors.push(ENV_CONFIG.messages.lowercase);
      }
      if (ENV_CONFIG.requireNumbers && !hasNumbers) {
        errors.push(ENV_CONFIG.messages.numbers);
      }
      if (ENV_CONFIG.requireSpecialChar && !hasSpecialChar) {
        errors.push(ENV_CONFIG.messages.specialChar);
      }

      if (errors.length > 0) {
        return Promise.reject(new Error(errors.join(' ')));
      }

      return Promise.resolve();
    },
  },
];

/**
 * Types
 */

export type LoginPayload = {
  username: string;
  password: string;
  otp: string;
  otpAction: string;
  clientId: string;
  tokenId: string | '';
};

export type LoginResponse = {
  responseCode: number;
  message: string;
  status: 'SUCCESS' | 'ERROR';
  data: LoginData;
};

export type LoginData = {
  partnerId: string;
  username: string;
  jwtToken: string;
  source: string;
  sourceId: string;
  roleId: string;
  userId: string;
  isAdmin: string;
  isMFAEnabled: boolean;
};

export type UserData = {
  username: string;
  source: string;
};

export type LogoutResponse = boolean;

export type UserEmailAvailabilityResponse = boolean;

/**
 * Validations
 */

// Define your OTP validation rules
const otpValidationRules = [
  {
    required: true,
    message: 'Please enter your OTP!',
  },
  {
    // Regex for exactly 6 alphanumeric characters (letters and digits)
    pattern: /^[a-zA-Z0-9]{6}$/,
    message: 'OTP must be 6 alphanumeric characters long (A-Z, a-z, 0-9).',
  },
  {
    len: 6, // Ant Design's built-in length validation
    message: 'OTP must be exactly 6 characters.',
  },
];

// 2. Simple Rule for Login Screen
export const loginPasswordRules: Rule[] = [
  { required: true, message: 'Please enter password' },
];

export const useLoginValidation = () =>
  useMemo(
    () =>
      createValidation<LoginPayload>({
        username: [emailRule],
        //password: [{ required: true, type: 'string' }],
        password: loginPasswordRules, // <-- Updated here
        //otp: [{ required: true, type: 'string' }],
        otp: otpValidationRules,
        otpAction: [{ required: true, type: 'string' }],
        clientId: [{ required: true, type: 'string', message: 'Client ID is required.' }],
        tokenId: [{ required: false, type: 'string' }],
      }),
    []
  );

// 1. Updated logoutApi using raw axios to bypass the helper's parameter limits
// export const logoutApi = async () => {
//   const token = sessionStorage.getItem('token');

//   // We use raw axios here because your 'post' helper doesn't support custom headers as the 3rd arg
//   return axios.post('/loginmodule/logout', {}, {
//     headers: {
//       'Content-Type': 'application/json', // Matches the Java requirement
//       Authorization: `Bearer ${token}`
//     }
//   });
// };

// export const logoutApi = async () => {
//   const token = sessionStorage.getItem('token');
//   const url = '/loginmodule/logout';

//   console.log(`[Logout API] Initiating request to: ${url}`);
//   console.log(`[Logout API] Authorization token present: ${!!token}`);

//   try {
//     const response = await axios.post(url, {}, {
//       headers: {
//         'Content-Type': 'application/json',
//         'Authorization': `Bearer ${token}`
//       }
//     });

//     console.log(`[Logout API] Success!`, response.status);
//     return response;

//   } catch (error: unknown) {
//     // Check if the error is specifically an Axios error
//     if (axios.isAxiosError(error)) {
//       // TypeScript now knows 'error' has .response and .message
//       console.error(`[Logout API] Error:`, error.response?.status || error.message);
//       if (error.response) {
//         console.error(`[Logout API] Server Response Data:`, error.response.data);
//       }
//     } else {
//       // Handles generic errors (like syntax errors or network failures)
//       console.error(`[Logout API] Non-Axios Error:`, error);
//     }
//     throw error;
//   }
// };

export const logoutApi = async (username: string) => {
  const token = sessionStorage.getItem('token');

  // Construct the URL with the username as a query parameter
  //const url = `https://uapiepayment.avanse.com/api/avanse/loginmodule/logout?username=${encodeURIComponent(username)}`;
  const url = `https://apiepayment.avanse.com/api/avanse/loginmodule/logout?username=${encodeURIComponent(username)}`;

  console.log(`[Logout API] Method: GET, Requesting logout for: ${username}`);

  try {
    const response = await axios.get(url, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    console.log(`[Logout API] Success!`, response.status);
    return response;

  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.error(`[Logout API] Error: ${error.response?.status} - ${error.response?.data?.message || error.message}`);
    }
    throw error;
  }
};

/**
 * Endpoints
 */

export const login = (payload: LoginPayload) =>
  post<LoginResponse>('/loginmodule/authenticate', payload);

// export const relogin = (username: string) =>
//   get<LoginResponse>('/loginmodule/authenticate?Username=' + username);

//export const logout = () => post<LogoutResponse>('/auth/logout');

export const useFetchUserEmailAvailability = (email: string) => {
  const { data } = useSWR<UserEmailAvailabilityResponse>(
    /\S+@\S+\.\S+/u.test(email) ? `/auth/email-availability/${email}` : null
  );

  return data ?? true;
};
